import { getToken, onMessage } from 'firebase/messaging'
import { doc, setDoc, getDoc, addDoc, collection, query, where, orderBy, limit, getDocs, arrayUnion } from 'firebase/firestore'
import { messaging, db } from './firebase'

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY

const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent) && /Safari/.test(navigator.userAgent)
const isStandalone = window.matchMedia('(display-mode: standalone)').matches

export const requestNotificationPermission = async () => {
  try {
    console.log('=== REQUESTING NOTIFICATION PERMISSION ===')
    console.log('iOS:', isIOS, 'Standalone:', isStandalone)
    
    // Check if iOS Safari without Home Screen
    if (isIOS && !isStandalone) {
      return { 
        success: false, 
        error: 'iOS_PWA',
        message: 'Add to Home Screen first: Tap Share button → Add to Home Screen, then enable notifications in Settings > Safari > Notifications' 
      }
    }
    
    // Check if Notification API is available
    const notificationSupported = typeof window !== 'undefined' && 'Notification' in window && typeof Notification.requestPermission === 'function'
    console.log('Notification API supported:', notificationSupported)
    
    if (!notificationSupported) {
      return { success: false, error: 'Notifications not supported on this browser' }
    }
    
    console.log('Calling Notification.requestPermission()...')
    const permission = await Notification.requestPermission()
    console.log('Permission result:', permission)
    
    if (permission !== 'granted') {
      return { success: false, error: 'Permission denied: ' + permission }
    }
    
    console.log('Checking messaging:', messaging)
    if (!messaging) {
      console.error('Messaging not initialized')
      return { success: false, error: 'Messaging not initialized' }
    }
    
    console.log('VAPID_KEY:', VAPID_KEY ? 'exists' : 'missing')
    if (!VAPID_KEY) {
      console.error('VAPID_KEY is missing in environment variables')
      return { success: false, error: 'VAPID_KEY missing' }
    }
    
    console.log('Getting FCM token...')
    const token = await getToken(messaging, { vapidKey: VAPID_KEY })
    console.log('Token obtained:', token.substring(0, 30) + '...')
    
    return { token, success: true }
  } catch (error) {
    console.error('=== NOTIFICATION ERROR ===')
    console.error('Error:', error)
    console.error('Error name:', error.name)
    console.error('Error message:', error.message)
    return { success: false, error: error.message }
  }
}

export const saveDeviceToken = async (userId, token) => {
  console.log('=== SAVING DEVICE TOKEN ===')
  console.log('User ID:', userId)
  console.log('Token:', token ? token.substring(0, 20) + '...' : 'missing')
  console.log('DB:', !!db)
  
  if (!db) {
    console.error('Database not available')
    return { success: false, error: 'Database not available' }
  }
  
  try {
    const tokenRef = doc(db, 'deviceTokens', userId)
    await setDoc(tokenRef, {
      tokens: arrayUnion(token),
      userId,
      updatedAt: new Date().toISOString(),
      platform: 'web'
    }, { merge: true })
    console.log('Token saved successfully!')
    return { success: true }
  } catch (error) {
    console.error('Error saving token:', error)
    console.error('Error details:', error.code, error.message)
    return { success: false, error: error.message }
  }
}

export const getDeviceToken = async (userId) => {
  try {
    const tokenRef = doc(db, 'deviceTokens', userId)
    const snapshot = await getDoc(tokenRef)
    if (snapshot.exists()) {
      return snapshot.data().tokens
    }
    return null
  } catch (error) {
    console.error('Error getting token:', error)
    return null
  }
}

export const createNotification = async (notification) => {
  try {
    // Basic dedupe: avoid creating the same notification for the same user
    // within a short time window (30 seconds for match_invite, 10 seconds otherwise). Match on userId, type,
    // matchId (if present) and senderId (if present).
    const windowMs = notification.type === 'match_invite' ? 30000 : 10000
    const cutoff = new Date(Date.now() - windowMs).toISOString()

    const notifCollection = collection(db, 'notifications')

    // Build query: at minimum filter by userId and type
    let q = query(notifCollection, where('userId', '==', notification.userId), where('type', '==', notification.type), orderBy('createdAt', 'desc'), limit(1))

    // If matchId is provided, include it in the query
    if (notification.matchId) {
      q = query(notifCollection, where('userId', '==', notification.userId), where('type', '==', notification.type), where('matchId', '==', notification.matchId), orderBy('createdAt', 'desc'), limit(1))
    }

    const recent = await getDocs(q)
    if (!recent.empty) {
      const docData = recent.docs[0].data()
      const sameSender = String(docData.extra?.senderId || '') === String(notification.extra?.senderId || '')
      const sameMessageKey = String(docData.messageI18nKey || '') === String(notification.messageI18nKey || '')
      if (sameSender && sameMessageKey && docData.createdAt >= cutoff) {
        // Consider this a duplicate — skip creating a new doc
        console.debug('Skipping duplicate notification', { notification })
        return recent.docs[0]
      }
    }

    return addDoc(notifCollection, {
      read: false,
      createdAt: new Date().toISOString(),
      ...notification
    })
  } catch (err) {
    console.error('createNotification error:', err)
    // Fall back to trying to create the notification to avoid silent loss
    try {
      return addDoc(collection(db, 'notifications'), {
        read: false,
        createdAt: new Date().toISOString(),
        ...notification
      })
    } catch (e) {
      console.error('Fallback createNotification failed:', e)
      throw e
    }
  }
}

export const onForegroundMessage = (callback) => {
  return onMessage(messaging, (payload) => {
    console.log('Foreground message received:', payload)
    if (document.visibilityState === 'visible') {
      callback(payload)
    }
  })
}

export const initializeNotifications = async (userId, onNotification) => {
  console.log('=== INITIALIZING NOTIFICATIONS ===')
  console.log('User ID:', userId)
  
  // Skip if already granted
  if (Notification.permission === 'granted') {
    console.log('Permission already granted, checking for existing token...')
    // Check if token exists in Firestore
    const existingToken = await getDeviceToken(userId)
    if (existingToken) {
      console.log('Existing token found, setting up handler')
      const unsubscribe = onForegroundMessage((payload) => {
        if (onNotification) onNotification(payload)
      })
      return { success: true }
    }
  }
  
  // Request permission and get token
  const result = await requestNotificationPermission()
  console.log('Permission result:', result)
  
  if (!result || !result.success) {
    console.log('Failed to get permission or token:', result?.error)
    return { success: false, error: result?.error || 'Unknown error' }
  }
  
  console.log('5. Saving device token...')
  try {
    const saveResult = await saveDeviceToken(userId, result.token)
    if (!saveResult.success) {
      return { success: false, error: 'Failed to save token: ' + saveResult.error }
    }
  } catch (e) {
    console.error('Error saving token:', e)
    return { success: false, error: 'Failed to save token: ' + e.message }
  }
  
  console.log('6. Setting up foreground message handler...')
  try {
    const unsubscribe = onForegroundMessage((payload) => {
      if (onNotification) onNotification(payload)
    })
    console.log('=== NOTIFICATIONS INITIALIZED SUCCESSFULLY ===')
    return { success: true }
  } catch (e) {
    console.error('Error setting up handler:', e)
    return { success: false, error: 'Failed to setup handler: ' + e.message }
  }
}